import React from "react";
import Button from "components/button";
import Icon from "components/icon";

interface ListHeaderProps {
  t: any;
  onBack: () => void;
  // Bulk action props
  totalSelected: number;
  isBulkOpen: boolean;
  setIsBulkOpen: (open: boolean) => void;
  isBulkDisabled?: boolean;
  bulkRef: React.RefObject<HTMLDivElement>;
  allowedBulkActions: string[];
  stagedBulkAction: string | null;
  setStagedBulkAction: (action: string | null) => void;
  onBulkStatusUpdate: (action: string) => void;
  getBulkActionLabel: (action: string | null) => string;
}

const ListHeader: React.FC<ListHeaderProps> = ({
  t,
  onBack,
  totalSelected,
  isBulkOpen,
  setIsBulkOpen,
  isBulkDisabled,
  bulkRef,
  allowedBulkActions,
  stagedBulkAction,
  setStagedBulkAction,
  onBulkStatusUpdate,
  getBulkActionLabel,
}) => {
  return (
    <div className="flex flex-col gap-6">
      <div 
        className="flex items-center gap-2 cursor-pointer w-fit text-text-primary hover:text-primary-500 transition-colors"
        onClick={onBack}
      >
        <Icon name="arrow-left" size={20} />
        <h2 className="text-xl font-bold">Draft</h2>
      </div>

      <div className="flex items-center gap-4 h-[40px]">
        {totalSelected > 0 && (allowedBulkActions.length > 0 || isBulkDisabled) && (
          <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2 duration-200">
            <div className="relative" ref={bulkRef}>
              <Button
                type="button"
                variant="outline"
                color="tertiary"
                size="medium"
                iconRight={<Icon name={isBulkOpen ? "chevron-up" : "chevron-down"} size={16} />}
                onClick={() => setIsBulkOpen(!isBulkOpen)}
                disabled={isBulkDisabled || allowedBulkActions.length === 0}
                data-testid="btn-bulk-actions"
              >
                {getBulkActionLabel(stagedBulkAction)}
              </Button>

              {isBulkOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] rounded-xl border border-border bg-white py-1 shadow-lg">
                  {allowedBulkActions.map((action) => (
                    <button
                      key={action}
                      type="button"
                      onClick={() => {
                        setStagedBulkAction(action);
                        setIsBulkOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-greyScale-90 transition-colors"
                    >
                      {action === "Inactive" ? t("bulk-action-deactivate") : 
                       action === "Available" ? t("bulk-action-activate") : 
                       action === "Maintenance" ? t("bulk-action-maintenance") :
                       action === "Delete" ? t("bulk-action-delete") :
                       action}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button
              type="button"
              variant="fill"
              color="secondary"
              size="medium"
              disabled={isBulkDisabled || !stagedBulkAction}
              onClick={() => stagedBulkAction && onBulkStatusUpdate(stagedBulkAction)}
              data-testid="btn-apply-data"
            >
              {t("btn-apply-data")}
            </Button>
          </div>
        )}

        <span className="text-sm font-medium text-text-secondary">
          {t("selected-count", { count: totalSelected })}
        </span>
      </div>
    </div>
  );
};

export default ListHeader;
